<template>
  <section class="admin-content" id="telemedicine-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-video" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Telemedicine Consultations</h4>
                <small style="opacity: .8;">{{ subtitle }}</small>
              </div>
            </div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <router-link v-if="isAgency" to="/telemedicine/network" class="btn btn-outline-light btn-sm">
                <i class="fe fe-users mr-1"></i>Virtual Practice Network
              </router-link>
              <button v-if="canSend" type="button" class="btn btn-light btn-sm" @click="sendOpen = true">
                <i class="fe fe-send mr-1"></i>Send for telemedicine
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <!-- Telemedicine doctors: their own specializations (shown to facilities when they book) -->
          <SpecializationsCard v-if="isTelemedicineDoctor" :user-id="myId" editable title="My specializations" />
          <AvailabilityCard v-if="isTelemedicineDoctor" />

          <!-- Status counts -->
          <div class="row mb-3">
            <div class="col-6 col-md-3 mb-2" v-for="s in statusCards" :key="s.key">
              <button type="button" class="stat-card" :class="{ active: filters.status === s.key }" :style="{ borderLeftColor: s.color }"
                @click="setStatus(s.key)">
                <span class="stat-count" :style="{ color: s.color }">{{ counts[s.key] || 0 }}</span>
                <span class="stat-label">{{ s.label }}</span>
              </button>
            </div>
          </div>

          <!-- Billing settings (agency) -->
          <div class="card shadow-sm settings-card mb-3" v-if="isAgency && settings">
            <div class="card-body d-flex flex-wrap align-items-center" style="gap: 16px;">
              <div class="flex-fill">
                <div class="font-weight-bold"><i class="fe fe-settings mr-1"></i>Telemedicine billing</div>
                <div class="small text-muted" v-if="!editingSettings">
                  <template v-if="!settings.enabled">Telemedicine is switched off; nobody can send patients.</template>
                  <template v-else-if="settings.billing_mode === 'claim' && settings.service">
                    Paid: telemedicine providers claim <strong>{{ settings.service.description }}</strong>
                    ({{ money(settings.service.price) }}) per consultation, plus what they record, through normal claims vetting.
                  </template>
                  <template v-else>Free: consultations are not paid (e.g. volunteer doctors, outreach).</template>
                </div>
              </div>
              <button v-if="canEditSettings && !editingSettings" type="button" class="btn btn-sm btn-outline-primary" @click="startEditSettings">Change</button>

              <form v-if="editingSettings" class="w-100" @submit.prevent="saveSettings">
                <div class="form-row align-items-end">
                  <div class="form-group col-md-3">
                    <div class="custom-control custom-switch">
                      <input id="tm-enabled" v-model="settingsForm.enabled" type="checkbox" class="custom-control-input" />
                      <label class="custom-control-label" for="tm-enabled">Telemedicine enabled</label>
                    </div>
                  </div>
                  <div class="form-group col-md-3">
                    <label for="tm-billing" class="small font-weight-bold">Billing</label>
                    <select id="tm-billing" v-model="settingsForm.billing_mode" class="form-control form-control-sm">
                      <option value="free">Free (no payment)</option>
                      <option value="claim">Paid through claims</option>
                    </select>
                  </div>
                  <div class="form-group col-md-4" v-if="settingsForm.billing_mode === 'claim'">
                    <label for="tm-service" class="small font-weight-bold">Consultation service (from your services list)</label>
                    <v-select v-model="settingsForm.service_id" input-id="tm-service" :options="services" label="description"
                      :reduce="s => s.id" placeholder="e.g. Telemedicine consultation" />
                  </div>
                  <div class="form-group col-md-2 d-flex" style="gap: 6px;">
                    <button type="submit" class="btn btn-sm btn-submit">Save</button>
                    <button type="button" class="btn btn-sm btn-light" @click="editingSettings = false">Cancel</button>
                  </div>
                </div>
                <small class="text-muted" v-if="settingsForm.billing_mode === 'claim'">
                  Add a "Telemedicine consultation" item with its price under Services first if it isn't in the list.
                </small>
              </form>
            </div>
          </div>

          <!-- Filters -->
          <div class="card shadow-sm mb-3">
            <div class="card-body d-flex flex-wrap align-items-end" style="gap: 10px;">
              <div v-if="isTelemedicineDoctor === false && !isAgency">
                <label for="tm-dir" class="small font-weight-bold d-block">Show</label>
                <select id="tm-dir" v-model="filters.direction" class="form-control form-control-sm" @change="load(1)">
                  <option value="">All</option>
                  <option value="sent">Sent by my facility</option>
                  <option value="received">Received</option>
                </select>
              </div>
              <div v-if="isAgency">
                <label for="tm-fac" class="small font-weight-bold d-block">Telemedicine provider</label>
                <select id="tm-fac" v-model="filters.provider_id" class="form-control form-control-sm" @change="load(1)">
                  <option value="">All</option>
                  <option v-for="f in facilities" :key="f.id" :value="f.id">{{ f.agency_name }}</option>
                </select>
              </div>
              <div v-if="isAgency">
                <label for="tm-spec" class="small font-weight-bold d-block">Specialization</label>
                <select id="tm-spec" v-model="specializationFilter" class="form-control form-control-sm" @change="load(1)">
                  <option value="">All</option>
                  <option v-for="s in specializationOptions" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>
              <div>
                <label for="tm-from" class="small font-weight-bold d-block">From</label>
                <input id="tm-from" v-model="filters.from" type="date" class="form-control form-control-sm" />
              </div>
              <div>
                <label for="tm-to" class="small font-weight-bold d-block">To</label>
                <input id="tm-to" v-model="filters.to" type="date" class="form-control form-control-sm" />
              </div>
              <button type="button" class="btn btn-sm btn-submit" @click="load(1)"><i class="fe fe-filter mr-1"></i>Apply</button>
              <button type="button" class="btn btn-sm btn-light" @click="clearFilters">Clear</button>
              <button type="button" class="btn btn-sm btn-outline-success ml-auto" @click="exportCsv" :disabled="!rows.length">
                <i class="fe fe-download mr-1"></i>CSV
              </button>
            </div>
          </div>

          <!-- List -->
          <div class="card shadow-sm table-card">
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background:#f8fafc;">
                  <tr>
                    <th class="table-th">Date</th>
                    <th class="table-th">Patient</th>
                    <th class="table-th">Telemedicine provider</th>
                    <th class="table-th">Sent by</th>
                    <th class="table-th">Status</th>
                    <th class="table-th">Outcome</th>
                    <th class="table-th"><span class="sr-only">Open</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in rows" :key="c.id" class="table-row">
                    <td class="px-4 py-3 text-nowrap">{{ c.appointDate }}<div class="small text-muted">{{ (c.appoinTime || '').slice(0, 5) }}</div></td>
                    <td class="py-3">
                      <div class="font-weight-bold" style="font-size:.88rem;">{{ c.patient_name || '—' }}</div>
                      <div class="small text-muted">{{ c.patient_id_card }}<span v-if="c.patient_type === 'Dependent'"> · dependant</span></div>
                      <div class="small text-muted text-truncate" style="max-width: 260px;" :title="c.reason">{{ c.reason }}</div>
                    </td>
                    <td class="py-3 small">
                      {{ c.provider ? c.provider.agency_name : '—' }}
                      <div class="text-muted" v-if="c.professional">Dr {{ c.professional.firstname }} {{ c.professional.lastname }}</div>
                      <div class="text-muted" v-if="specsOf(c.provider_id)">{{ specsOf(c.provider_id) }}</div>
                    </td>
                    <td class="py-3 small">{{ c.referring_provider ? c.referring_provider.agency_name : 'Agency' }}</td>
                    <td class="py-3"><span :class="'status-badge ' + (c.status || 'pending')">{{ c.status || 'pending' }}</span></td>
                    <td class="py-3 small">
                      <span v-if="c.service_summary_id" class="text-success"><i class="fe fe-file-text mr-1"></i>Encounter</span>
                      <span v-if="c.claim_id" class="d-block text-primary"><i class="fe fe-credit-card mr-1"></i>Claimed</span>
                      <span v-if="!c.service_summary_id && !c.claim_id" class="text-muted">—</span>
                    </td>
                    <td class="py-3 pr-4 text-right">
                      <router-link :to="`/appointment/${c.id}`" class="btn btn-sm btn-view"><i class="fe fe-eye mr-1"></i>Open</router-link>
                    </td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td colspan="7" class="text-center py-5 text-muted">
                      <i class="fe fe-video" style="font-size:2rem;opacity:.3"></i>
                      <p class="mt-2 mb-0">{{ loading ? 'Loading…' : 'No telemedicine consultations' }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="lastPage > 1" class="d-flex justify-content-center py-3">
              <button type="button" class="btn btn-sm btn-light mr-2" :disabled="page <= 1" @click="load(page - 1)">Previous</button>
              <span class="small text-muted align-self-center">Page {{ page }} of {{ lastPage }} · {{ total }} consultations</span>
              <button type="button" class="btn btn-sm btn-light ml-2" :disabled="page >= lastPage" @click="load(page + 1)">Next</button>
            </div>
          </div>
        </div>
      </section>

      <SendTelemedicine :open="sendOpen" @close="sendOpen = false" @sent="onSent" />
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import SendTelemedicine from "./SendTelemedicine.vue";
import SpecializationsCard from "./SpecializationsCard.vue";
import AvailabilityCard from "./AvailabilityCard.vue";

function stored(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (e) {
    return null;
  }
}

export default {
  components: { Navbar, SendTelemedicine, SpecializationsCard, AvailabilityCard },
  data() {
    const user = stored("user") || {};
    return {
      user,
      agency: stored("agency"),
      rows: [],
      counts: {},
      page: 1,
      lastPage: 1,
      total: 0,
      loading: false,
      facilities: [],
      services: [],
      settings: null,
      canEditSettings: false,
      editingSettings: false,
      settingsForm: { enabled: true, billing_mode: "free", service_id: null },
      sendOpen: false,
      specializationFilter: "",
      filters: { status: "", direction: "", provider_id: "", from: "", to: "" },
      statusCards: [
        { key: "pending", label: "Pending", color: "#f59e0b" },
        { key: "accepted", label: "Accepted", color: "#3b82f6" },
        { key: "completed", label: "Completed", color: "#10b981" },
        { key: "declined", label: "Declined", color: "#ef4444" },
      ],
    };
  },
  computed: {
    isAgency() {
      return this.user.type === "shis" || this.user.type === "employee";
    },
    isFacility() {
      return this.user.type === "provider" || this.user.type === "provider_employee";
    },
    // Known once the facilities list is loaded: is the signed-in facility a telemedicine provider?
    isTelemedicineDoctor() {
      if (!this.isFacility) return false;
      const facilityId = this.user.type === "provider" ? this.user.id : this.user.institutional_id;
      return this.facilities.some((f) => Number(f.id) === Number(facilityId));
    },
    // Doctors (provider_employee) keep their own list; the facility account edits the facility's.
    myId() {
      return this.user.id;
    },
    canSend() {
      return this.isAgency || (this.isFacility && !this.isTelemedicineDoctor);
    },
    // Specializations of the agency's telemedicine providers, from the facilities list.
    specializationOptions() {
      const names = new Set();
      this.facilities.forEach((f) => (f.specializations || []).forEach((s) => names.add(s.name)));
      return Array.from(names).sort();
    },
    subtitle() {
      if (this.isAgency) return "Every virtual consultation in the agency, for quality assurance and payment";
      if (this.isTelemedicineDoctor) return "Patients sent to you for virtual consultation";
      return "Patients your facility sent for virtual consultation";
    },
  },
  created() {
    this.axios.get("/api/v1/auth/telemedicine/facilities").then((res) => (this.facilities = res.data.data || []));
    if (this.isAgency) this.loadSettings();
    this.load(1);
  },
  methods: {
    money(v) {
      return "₦" + Number(v || 0).toLocaleString("en-NG");
    },
    specsOf(facilityId) {
      const f = this.facilities.find((x) => Number(x.id) === Number(facilityId));
      return f && f.specializations && f.specializations.length ? f.specializations.map((s) => s.name).join(", ") : "";
    },
    params(page) {
      const p = { page, per_page: 50 };
      // A specialization narrows to the providers that offer it.
      if (this.specializationFilter) {
        const ids = this.facilities.filter((f) => (f.specializations || []).some((s) => s.name === this.specializationFilter)).map((f) => f.id);
        if (!this.filters.provider_id) p.provider_ids = ids.length ? ids : [0];
      }
      Object.entries(this.filters).forEach(([k, v]) => {
        if (v !== "" && v !== null) p[k] = v;
      });
      return p;
    },
    load(page) {
      this.loading = true;
      this.axios
        .get("/api/v1/auth/telemedicine/consultations", { params: this.params(page) })
        .then((res) => {
          this.rows = res.data.data || [];
          this.counts = res.data.counts || {};
          this.page = res.data.current_page || 1;
          this.lastPage = res.data.last_page || 1;
          this.total = res.data.total || 0;
        })
        .finally(() => (this.loading = false));
    },
    setStatus(status) {
      this.filters.status = this.filters.status === status ? "" : status;
      this.load(1);
    },
    clearFilters() {
      this.filters = { status: "", direction: "", provider_id: "", from: "", to: "" };
      this.load(1);
    },
    onSent() {
      this.load(1);
    },
    loadSettings() {
      this.axios.get("/api/v1/auth/telemedicine/settings").then((res) => {
        this.settings = res.data.data;
        this.canEditSettings = res.data.can_edit;
      });
    },
    startEditSettings() {
      this.settingsForm = { enabled: this.settings.enabled, billing_mode: this.settings.billing_mode, service_id: this.settings.service_id };
      this.editingSettings = true;
      if (!this.services.length && this.agency) {
        this.axios.get(`/api/v1/auth/service-agency/${this.agency.id}`).then((res) => {
          const data = res.data;
          this.services = Array.isArray(data) ? data : data.data || [];
        });
      }
    },
    saveSettings() {
      this.axios
        .put("/api/v1/auth/telemedicine/settings", this.settingsForm)
        .then((res) => {
          this.settings = res.data.data;
          this.editingSettings = false;
          this.$toasted.success("Telemedicine billing saved", { position: "top-center", duration: 3000 });
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {};
          const msg = data.errors ? Object.values(data.errors).flat().join(" ") : data.message || "Could not save";
          this.$toasted.error(msg, { position: "top-center", duration: 4000 });
        });
    },
    exportCsv() {
      const header = ["Date", "Time", "Patient", "ID number", "Dependant", "Reason", "Telemedicine provider", "Doctor", "Sent by", "Status", "Encounter", "Claimed"];
      const lines = this.rows.map((c) => [
        c.appointDate, (c.appoinTime || "").slice(0, 5), c.patient_name, c.patient_id_card, c.patient_type === "Dependent" ? "Yes" : "No",
        c.reason, c.provider ? c.provider.agency_name : "", c.professional ? `${c.professional.firstname} ${c.professional.lastname}` : "",
        c.referring_provider ? c.referring_provider.agency_name : "Agency", c.status, c.service_summary_id ? "Yes" : "No", c.claim_id ? "Yes" : "No",
      ]);
      const esc = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [header, ...lines].map((r) => r.map(esc).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `telemedicine-consultations-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.stat-card { width: 100%; text-align: left; background: #fff; border: none; border-left: 4px solid; border-radius: 10px; padding: 12px 16px; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
.stat-card.active { box-shadow: 0 0 0 2px #7c3aed; }
.stat-count { display: block; font-size: 1.6rem; font-weight: 700; }
.stat-label { font-size: .75rem; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; }
.settings-card { border-radius: 12px; border: none; border-left: 4px solid #7c3aed; }
.table-card { border-radius: 12px; border: none; }
.table-th { font-size: .73rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; }
.table-row:hover { background: #f8fafc; }
.status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: .75rem; font-weight: 600; text-transform: capitalize; }
.status-badge.pending { background: #fef3c7; color: #92400e; }
.status-badge.accepted { background: #dbeafe; color: #1e40af; }
.status-badge.completed { background: #d1fae5; color: #065f46; }
.status-badge.declined { background: #fee2e2; color: #991b1b; }
.btn-submit { background: linear-gradient(135deg, #4c1d95, #7c3aed); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #4c1d95; background: #fff; font-size: .8rem; }
</style>
